import { Form, Tabs } from 'antd';
import { UploadFile } from 'antd/es/upload/interface';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useConfigurationCtx } from '../GeneralSettings/ConfigurationContext';
import enResource from '../locales/en/Resources.json';
import fiResource from '../locales/fi/Resources.json';
import plResource from '../locales/pl/Resources.json';
import svResource from '../locales/sv/Resources.json';
import { IGeneralSettings } from '../models/generalSettingsModel';
import { normFileAsDataUrl } from '../utils/fileListContainer';
import { IConfigurationState } from './ConfigurationContext';
import { ConfigurationProvider } from './ConfigurationProvider';
import { LocatorConfiguration } from './LocatorConfiguration';
import LogoSettingsTab from './LogoSettingsTab';
import PortalConfiguration from './PortalConfiguration';
import { ServerConfiguration } from './ServerConfiguration';
import TabPaneWrapper from './TabPaneWrapper';

const StyledTabs = styled(Tabs)`
  height: 100%;
  &&&& .ant-tabs-tabpane {
    overflow-y: hidden;
  }
  &&& .ant-tabs-bar {
    margin-top: 15px;
    height: calc(100% - 40px);
  }
`;

const TabPane = styled(Tabs.TabPane)`
  &&& {
    max-height: 100%;
  }
`;

export interface IGeneralSettingsProps {
  usePortal?: boolean;
  hideUseInMap?: boolean;
}

export const GeneralSettings = ({
  generalSettings,
  mapConfiguration,
  getLocatorSuggestions,
  getProxyUrl,
  testServerUrl,
  usePortal = false,
  hideUseInMap = false
}: IConfigurationState & IGeneralSettingsProps) => {
  return (
    <ConfigurationProvider
      generalSettings={generalSettings}
      mapConfiguration={mapConfiguration}
      getLocatorSuggestions={getLocatorSuggestions}
      getProxyUrl={getProxyUrl}
      testServerUrl={testServerUrl}
    >
      <GeneralSettingsWrapper usePortal={usePortal} hideUseInMap={hideUseInMap} />
    </ConfigurationProvider>
  );
};

export interface IGeneralSettingsForm extends Omit<IGeneralSettings, 'municipalityIcon' | 'municipalityLogo'> {
  municipalityIcon: UploadFile[];
  municipalityLogo: UploadFile[];
}

const GeneralSettingsWrapper = ({ usePortal, hideUseInMap }: IGeneralSettingsProps) => {
  const i18n = useTranslation()[1];
  i18n.addResources('en', 'geosecma-settings', enResource.settings);
  i18n.addResources('sv', 'geosecma-settings', svResource.settings);
  i18n.addResources('fi', 'geosecma-settings', fiResource.settings);
  i18n.addResources('pl', 'geosecma-settings', plResource.settings);
  const [activeTab, setActiveTab] = React.useState('municipalityTab');
  const { generalSettings } = useConfigurationCtx();
  const { t } = useTranslation('geosecma-settings');
  const [form] = Form.useForm<IGeneralSettingsForm>();

  const onTabChange = (activeKey: string) => {
    setActiveTab(activeKey);
  };

  const onValuesChange = async (changedValues: Partial<IGeneralSettingsForm>, allValues: IGeneralSettingsForm) => {
    const { municipalityName, municipalityIcon, municipalityLogo, serverInfos, locators, ...rest } = changedValues;
    if (municipalityName) {
      generalSettings.setMunicipalityName(municipalityName);
    } else if (Array.isArray(municipalityIcon)) {
      if (municipalityIcon.length === 0) {
        generalSettings.setMunicipalityIcon(undefined);
      } else {
        const image = municipalityIcon[0];
        const base64String = await normFileAsDataUrl(image.originFileObj!);
        if (!base64String) {
          generalSettings.setMunicipalityIcon(undefined);
        } else {
          generalSettings.setMunicipalityIcon({
            ...image,
            content: base64String
          });
        }
      }
    } else if (Array.isArray(municipalityLogo)) {
      if (municipalityLogo.length === 0) {
        generalSettings.setMunicipalityLogo(undefined);
      } else {
        const image = municipalityLogo[0];
        const base64String = await normFileAsDataUrl(image.originFileObj!);
        if (!base64String) {
          generalSettings.setMunicipalityLogo(undefined);
        } else {
          generalSettings.setMunicipalityLogo({
            ...image,
            content: base64String
          });
        }
      }
    } else if (Array.isArray(serverInfos)) {
      generalSettings.setServerInfos(allValues.serverInfos);
    } else if (Array.isArray(locators)) {
      generalSettings.setLocators(allValues.locators);
    } else {
      generalSettings.setValues(rest);
    }
  };

  const generalSettingsJson = React.useMemo(() => {
    const tempJson = generalSettings.toJSON();
    const municipalityIcons: UploadFile[] | undefined = tempJson.municipalityIcon
      ? [
          {
            ...tempJson.municipalityIcon,
            status: 'done',
            uid: tempJson.municipalityIcon.uid ?? '-1',
            name: tempJson.municipalityIcon.name ?? 'noname'
          }
        ]
      : undefined;
    const municipalityLogos: UploadFile[] | undefined = tempJson.municipalityLogo
      ? [
          {
            ...tempJson.municipalityLogo,
            status: 'done',
            uid: tempJson.municipalityLogo.uid ?? '-1',
            name: tempJson.municipalityLogo.name ?? 'noname'
          }
        ]
      : undefined;
    const newSettings = {
      ...tempJson,
      municipalityIcon: municipalityIcons,
      municipalityLogo: municipalityLogos
    };

    form.setFieldsValue(newSettings);

    return newSettings;
  }, [form, generalSettings]);

  return (
    <Form
      form={form}
      layout="vertical"
      hideRequiredMark
      initialValues={generalSettingsJson}
      onValuesChange={onValuesChange}
    >
      <StyledTabs tabPosition="left" defaultActiveKey="municipalityTab" activeKey={activeTab} onChange={onTabChange}>
        <TabPane tab={<span data-testid="municipalityTab">{t('municipalityTab')}</span>} key="municipalityTab">
          {activeTab === 'municipalityTab' ? (
            <TabPaneWrapper>
              <LogoSettingsTab form={form} />
            </TabPaneWrapper>
          ) : null}
        </TabPane>
        <TabPane tab={<span data-testid="serverInfosTab">{t('serverInfosTab')}</span>} key="serverInfos">
          {activeTab === 'serverInfos' ? (
            <TabPaneWrapper>
              <ServerConfiguration form={form} />
            </TabPaneWrapper>
          ) : null}
        </TabPane>
        {usePortal ? (
          <TabPane tab={<span data-testid="portalTab">{t('portalTab')}</span>} key="portal">
            {activeTab === 'portal' ? (
              <TabPaneWrapper>
                <PortalConfiguration />
              </TabPaneWrapper>
            ) : null}
          </TabPane>
        ) : null}
        <TabPane tab={<span data-testid="locatorsTab">{t('locators')}</span>} key="locators">
          {activeTab === 'locators' ? (
            <TabPaneWrapper>
              <LocatorConfiguration hideUseInMap={hideUseInMap} />
            </TabPaneWrapper>
          ) : null}
        </TabPane>
      </StyledTabs>
    </Form>
  );
};
